import { useState, useEffect } from 'react'
import api from '../services/api'

export interface ColaboradorIndividual {
  motcod: string
  motnome: string
}

export interface SelecaoColaboradoresValor {
  modo: 'todos' | 'categoria' | 'individual'
  catcat: string[]
  colaboradoresIndividuais: ColaboradorIndividual[]
  excluidos: string[]
}

interface CategoriaCargo {
  catcat: string
  label: string
}

interface ColaboradorBusca {
  motcod: string
  motnome: string
}

interface ColaboradorLote {
  motcod: string
  motnome: string
}

export function selecaoColaboradoresVazia(): SelecaoColaboradoresValor {
  return { modo: 'todos', catcat: [], colaboradoresIndividuais: [], excluidos: [] }
}

export default function SelecaoColaboradores({ valor, onChange }: {
  valor: SelecaoColaboradoresValor
  onChange: (valor: SelecaoColaboradoresValor) => void
}) {
  const [categorias, setCategorias] = useState<CategoriaCargo[]>([])
  const [buscaColaborador, setBuscaColaborador] = useState('')
  const [opcoesColaborador, setOpcoesColaborador] = useState<ColaboradorBusca[]>([])
  const [preview, setPreview] = useState<ColaboradorLote[]>([])
  const [carregandoPreview, setCarregandoPreview] = useState(false)
  const [mostrarLista, setMostrarLista] = useState(false)

  useEffect(() => {
    api.get('/cartao-ponto/categorias-cargo').then(r => setCategorias(r.data))
  }, [])

  useEffect(() => {
    // a lista resolvida (todos/categoria) muda, então exclusões de uma seleção anterior
    // não fazem mais sentido — reseta ao trocar modo, categorias ou a lista de colaboradores
    // específicos. No modo individual a prévia já é a própria lista escolhida, sem round-trip.
    onChange({ ...valor, excluidos: [] })
    if (valor.modo === 'individual') { setPreview(valor.colaboradoresIndividuais); return }
    if (valor.modo === 'categoria' && valor.catcat.length === 0) { setPreview([]); return }
    setCarregandoPreview(true)
    const params: Record<string, string> = { modo: valor.modo }
    if (valor.modo === 'categoria') params.catcat = valor.catcat.join(',')
    api.get('/cartao-ponto/colaboradores-lote', { params })
      .then(r => setPreview(r.data))
      .finally(() => setCarregandoPreview(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valor.modo, valor.catcat, valor.colaboradoresIndividuais])

  async function buscarColaborador(texto: string) {
    setBuscaColaborador(texto)
    if (texto.trim().length < 2) { setOpcoesColaborador([]); return }
    const r = await api.get('/cartao-ponto/colaboradores', { params: { busca: texto } })
    setOpcoesColaborador(r.data)
  }

  function selecionarColaborador(c: ColaboradorBusca) {
    setBuscaColaborador('')
    setOpcoesColaborador([])
    if (valor.colaboradoresIndividuais.some(x => x.motcod === c.motcod)) return
    onChange({ ...valor, colaboradoresIndividuais: [...valor.colaboradoresIndividuais, c] })
  }

  function removerIndividual(motcod: string) {
    onChange({ ...valor, colaboradoresIndividuais: valor.colaboradoresIndividuais.filter(c => c.motcod !== motcod) })
  }

  function toggleCategoria(catcat: string) {
    const novo = valor.catcat.includes(catcat) ? valor.catcat.filter(c => c !== catcat) : [...valor.catcat, catcat]
    onChange({ ...valor, catcat: novo })
  }

  function toggleExcluido(motcod: string) {
    const novo = valor.excluidos.includes(motcod)
      ? valor.excluidos.filter(c => c !== motcod)
      : [...valor.excluidos, motcod]
    onChange({ ...valor, excluidos: novo })
  }

  function marcarTodos(marcar: boolean) {
    onChange({ ...valor, excluidos: marcar ? [] : preview.map(c => c.motcod) })
  }

  const permiteExcluir = valor.modo === 'todos' || valor.modo === 'categoria'
  const selecionados = permiteExcluir ? preview.filter(c => !valor.excluidos.includes(c.motcod)).length : preview.length

  return (
    <div>
      <div className="field">
        <label>Colaboradores</label>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 4 }}>
          {([
            ['todos', 'Todos os colaboradores'],
            ['categoria', 'Grupo específico (cargo)'],
            ['individual', 'Colaborador específico'],
          ] as const).map(([modo, label]) => (
            <label key={modo} style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 400, cursor: 'pointer' }}>
              <input
                type="radio"
                checked={valor.modo === modo}
                onChange={() => onChange({ ...valor, modo })}
              />
              {label}
            </label>
          ))}
        </div>
      </div>

      {valor.modo === 'categoria' && (
        <div className="field" style={{ marginTop: 10 }}>
          <label>Categoria de cargo (catcat)</label>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 4 }}>
            {categorias.map(c => (
              <label key={c.catcat} style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 400, cursor: 'pointer' }}>
                <input type="checkbox" checked={valor.catcat.includes(c.catcat)} onChange={() => toggleCategoria(c.catcat)} />
                {c.label} <span style={{ color: '#9ca3af', fontFamily: 'monospace', fontSize: '0.75rem' }}>({c.catcat})</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {valor.modo === 'individual' && (
        <div className="field" style={{ marginTop: 10, position: 'relative', maxWidth: 420 }}>
          <label>Colaborador (código ou nome)</label>
          <input
            type="text"
            value={buscaColaborador}
            onChange={e => buscarColaborador(e.target.value)}
            placeholder="Digite o código ou o nome e selecione"
          />
          {opcoesColaborador.length > 0 && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
              background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6,
              maxHeight: 220, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            }}>
              {opcoesColaborador.map(c => (
                <div
                  key={c.motcod}
                  onClick={() => selecionarColaborador(c)}
                  style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '0.85rem' }}
                  onMouseDown={e => e.preventDefault()}
                >
                  <b>{c.motcod}</b> — {c.motnome}
                </div>
              ))}
            </div>
          )}

          {valor.colaboradoresIndividuais.length > 0 && (
            <div style={{
              marginTop: 8, maxHeight: 200, overflowY: 'auto', border: '1px solid #e5e7eb',
              borderRadius: 6, padding: '4px 10px', background: '#f9fafb',
            }}>
              {valor.colaboradoresIndividuais.map(c => (
                <div
                  key={c.motcod}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '4px 0', fontSize: '0.85rem' }}
                >
                  <span>{c.motcod} - {c.motnome}</span>
                  <button
                    type="button"
                    onClick={() => removerIndividual(c.motcod)}
                    title="Remover"
                    style={{ border: 'none', background: 'none', color: '#b91c1c', cursor: 'pointer', fontSize: '0.9rem', lineHeight: 1, padding: '0 4px' }}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div style={{ marginTop: 10, fontSize: '0.85rem', color: '#374151' }}>
        {carregandoPreview ? 'Calculando...' : (
          <>
            <b>{selecionados}</b> colaborador(es) selecionado(s)
            {permiteExcluir && valor.excluidos.length > 0 && (
              <span style={{ color: '#9ca3af' }}> ({valor.excluidos.length} excluído(s) manualmente de {preview.length})</span>
            )}
            {permiteExcluir && preview.length > 0 && (
              <button
                type="button"
                className="btn btn-outline btn-sm"
                style={{ marginLeft: 10 }}
                onClick={() => setMostrarLista(m => !m)}
              >
                {mostrarLista ? 'Ocultar lista' : 'Ver lista / desmarcar colaboradores'}
              </button>
            )}
          </>
        )}
        {permiteExcluir && mostrarLista && preview.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <div style={{ display: 'flex', gap: 10, marginBottom: 6 }}>
              <button type="button" className="btn btn-outline btn-sm" onClick={() => marcarTodos(true)}>Marcar todos</button>
              <button type="button" className="btn btn-outline btn-sm" onClick={() => marcarTodos(false)}>Desmarcar todos</button>
            </div>
            <div style={{
              maxHeight: 260, overflowY: 'auto', border: '1px solid #e5e7eb',
              borderRadius: 6, padding: '6px 10px', background: '#f9fafb',
            }}>
              {preview.map(c => {
                const excluido = valor.excluidos.includes(c.motcod)
                return (
                  <label key={c.motcod} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 0', cursor: 'pointer', color: excluido ? '#9ca3af' : undefined, textDecoration: excluido ? 'line-through' : undefined }}>
                    <input type="checkbox" checked={!excluido} onChange={() => toggleExcluido(c.motcod)} />
                    {c.motcod} - {c.motnome}
                  </label>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
